import { isCompanyActionEligible } from "../../helper/companyEligibility";
import {
  ALL_COLUMNS,
  getEnrichmentActionLabel,
  isLeadContactActionKey,
  isLeadContactColumnName,
} from "./EnrichmentGrid.helpers";
import {
  formatGeneratedColumnProgressLabel,
  GENERATED_CELL_STATUS,
  normalizeGeneratedCellStatus,
} from "../../helper/generatedColumn";
import {normalizeCompanyLocations} from "../../helper/spreadsheetView";
import {formatHeadcountBucket, formatRevenueBucket, isEstimatedMetric, metricSortValue} from "../../helper/metricBuckets";
import {industryLabel} from "../../helper/industry";
import { mergeFoundEmail } from "./foundEmail";

const MOBILE_PRIORITY_ORDER = ["name", "industry", "size", "location", "domain"];
const isLeadContactLayoutKey = (actionKey) => (
  isLeadContactActionKey(actionKey) || isLeadContactColumnName(actionKey)
);

export const buildGridColumns = ({
  activeColumnKeys,
  containerWidth,
  enrichmentColumns,
  isMobile,
  makeBaseColumn,
  makeEnrichmentColumn,
  renderHeaderCell,
  selectColumn,
  workspaceEnrichmentColumnLabels = {},
}) => {
  const priorityKeys = new Set(MOBILE_PRIORITY_ORDER);
  const visibleKeys = ALL_COLUMNS
    .map((column) => column.key)
    .filter((key) => activeColumnKeys.has(key));
  const nameFirstKeys = visibleKeys.includes("name")
    ? ["name", ...visibleKeys.filter((key) => key !== "name")]
    : visibleKeys;
  const orderedKeys = isMobile
    ? [
        ...MOBILE_PRIORITY_ORDER.filter((key) => nameFirstKeys.includes(key)),
        ...nameFirstKeys.filter((key) => !priorityKeys.has(key)),
      ]
    : nameFirstKeys;
  const baseColumns = orderedKeys.map(makeBaseColumn);
  // SelectColumn is fixed at 35px on desktop by react-data-grid. Reserving its
  // rendered width keeps the allocation pool and the grid tracks in sync.
  const selectWidth = isMobile ? 44 : 35;
  const columnsWithSelect = isMobile
    ? [
        {
          ...selectColumn,
          width: selectWidth,
          minWidth: selectWidth,
          maxWidth: selectWidth,
          frozen: true,
        },
        ...baseColumns,
      ]
    : [{...selectColumn, width: selectWidth}, ...baseColumns];
  const enrichmentDefinitions = enrichmentColumns.map((actionKey) =>
    makeEnrichmentColumn(
      actionKey,
      (Object.prototype.hasOwnProperty.call(workspaceEnrichmentColumnLabels, actionKey)
        ? workspaceEnrichmentColumnLabels[actionKey]
        : null) || getEnrichmentActionLabel(actionKey)
    )
  );
  const columnCount = baseColumns.length + enrichmentDefinitions.length || 1;
  const availableWidth = Math.max(0, containerWidth - selectWidth);
  const sharedWidth = Math.floor(availableWidth / columnCount);
  const desktopColumns = columnsWithSelect.concat(enrichmentDefinitions);

  if (!isMobile) {
    const minimumWidths = desktopColumns.slice(1).map((column) => {
      const actionKey = column.key.startsWith("enrich_")
        ? column.key.replace(/^enrich_/, "")
        : "";
      return column.key === "name"
        ? 220
        : isLongFormEnrichmentAction(actionKey)
          ? 450
          : isLeadContactLayoutKey(actionKey)
            ? 190
            : 160;
    });
    const totalMinimumWidth = minimumWidths.reduce((sum, width) => sum + width, 0);
    const remainingWidth = Math.max(0, availableWidth - totalMinimumWidth);
    const widthPerColumn = Math.floor(remainingWidth / columnCount);
    const remainder = remainingWidth % columnCount;

    return desktopColumns.map((column, index) => {
      if (index === 0) {
        return {
          ...column,
          renderHeaderCell: column.renderHeaderCell || renderHeaderCell,
        };
      }

      const width = minimumWidths[index - 1] + widthPerColumn + (index <= remainder ? 1 : 0);
      return {
        ...column,
        frozen: column.key === "name",
        width,
        renderHeaderCell: column.renderHeaderCell || renderHeaderCell,
      };
    });
  }

  return desktopColumns.map((column, index) => {
    if (index === 0) {
      return {
        ...column,
        renderHeaderCell: column.renderHeaderCell || renderHeaderCell,
      };
    }

    const isName = column.key === "name";
    const isSize = column.key === "size";
    const actionKey = column.key.startsWith("enrich_")
      ? column.key.replace(/^enrich_/, "")
      : "";
    const isLongForm = isLongFormEnrichmentAction(actionKey);
    const isLeadContact = isLeadContactLayoutKey(actionKey);
    let width = Math.max(isName ? 220 : isLeadContact ? 190 : 160, sharedWidth);

    if (isMobile) {
      if (isName) {
        width = Math.min(
          200,
          Math.max(160, Math.floor(availableWidth * 0.48) || 160)
        );
      } else if (isSize) {
        width = 152;
      } else if (isLongForm) {
        width = Math.max(260, sharedWidth || 260);
      } else {
        width = Math.max(148, Math.min(168, sharedWidth || 148));
      }
    } else if (isLongForm) {
      width = Math.max(450, sharedWidth * 1.75);
    }

    return {
      ...column,
      frozen: isName,
      width,
      minWidth: isMobile && (isName || isSize) ? width : column.minWidth,
      renderHeaderCell: column.renderHeaderCell || renderHeaderCell,
    };
  });
};

export const mergeWorkspaceColumns = (currentColumns, workspaceColumns) => {
  const incomingColumns = Array.isArray(workspaceColumns)
    ? workspaceColumns.filter(Boolean)
    : [];
  if (incomingColumns.length === 0) return currentColumns;

  const nextColumns = [...currentColumns];
  let changed = false;
  incomingColumns.forEach((column) => {
    if (!nextColumns.includes(column)) {
      nextColumns.push(column);
      changed = true;
    }
  });
  return changed ? nextColumns : currentColumns;
};

const workspaceEntriesMatch = (currentEntries, incomingEntries) => {
  const currentKeys = Object.keys(currentEntries);
  const incomingKeys = Object.keys(incomingEntries);
  return (
    incomingKeys.length === currentKeys.length
    && incomingKeys.every(
      (rowId) =>
        currentEntries[rowId]?.status === incomingEntries[rowId]?.status
        && currentEntries[rowId]?.result === incomingEntries[rowId]?.result
        && currentEntries[rowId]?.error === incomingEntries[rowId]?.error
    )
  );
};

export const mergeWorkspaceStatusMap = (
  currentStatusMap,
  workspaceColumns,
  workspaceStatusMap
) => {
  const nextStatusMap = {...(currentStatusMap || {})};
  let changed = false;

  workspaceColumns.forEach((column) => {
    const incomingEntries = workspaceStatusMap?.[column] || {};
    const currentEntries = nextStatusMap[column] || {};
    if (!workspaceEntriesMatch(currentEntries, incomingEntries)) {
      nextStatusMap[column] = {...incomingEntries};
      changed = true;
    }
  });

  return changed || !currentStatusMap ? nextStatusMap : currentStatusMap;
};

export const getLatestWorkspaceAction = (
  currentAction,
  workspaceColumns,
  workspaceStatusMap
) => {
  const newestAction = workspaceColumns[workspaceColumns.length - 1];
  const newestIsRunning = Object.values(workspaceStatusMap?.[newestAction] || {}).some(
    (entry) => entry?.status === "running"
  );
  return newestIsRunning ? newestAction : currentAction || newestAction;
};

export const buildCompanyIndex = (companies) => {
  const companyById = new Map();
  companies.forEach((company) => {
    if (company?.stagedId || company?.workspaceRowKey) {
      companyById.set(company.stagedId || company.workspaceRowKey, company);
    }
  });
  return companyById;
};

const getMostSpecificClassificationDescription = (classification) => {
  const levels = classification?.levels;
  if (!Array.isArray(levels)) return null;

  for (let index = levels.length - 1; index >= 0; index -= 1) {
    const description = levels[index]?.description;
    if (typeof description === "string" && description.trim()) {
      return description;
    }
  }

  return null;
};

const getClassificationCollectionDescription = (classifications) => {
  if (!Array.isArray(classifications)) return null;

  for (const classification of classifications) {
    const description = getMostSpecificClassificationDescription(classification);
    if (description) return description;
  }

  return null;
};

// industryLabel (FE #651, spec 2026-09-23 §5.3) reads bestClassification, then
// sourcedClassifications[0], then modeledClassifications[0], preferring a described NAICS
// level. The collection scans below only add the later entries of each list.
const getIndustryDescription = (company) =>
  industryLabel(company) ||
  getClassificationCollectionDescription(company.sourcedClassifications) ||
  getClassificationCollectionDescription(company.modeledClassifications) ||
  company.storedIndustry ||
  "N/A";

export const buildGridRows = (companies) =>
  companies.map((company) => {
    const locations = normalizeCompanyLocations(company);
    return {
      id: company.stagedId || company.workspaceRowKey,
      readOnly: !isCompanyActionEligible(company),
      name: company.primaryName || "N/A",
      logo: company.primaryLogo || null,
      description: company.descriptive?.[0] || "N/A",
      // Keep this resolver when integrating origin/design-preview: its inlined row builder at
      // EnrichmentGrid.js:548 is a known pending regression for FE #557.
      industry: getIndustryDescription(company),
      size: metricSortValue(company.harmonizedHeadcount) ?? 0,
      // Saved-search rows carry the backend's display text (BE #746); search rows do not.
      sizeLabel: company.headcountDisplay || formatHeadcountBucket(company.harmonizedHeadcount),
      sizeEstimated: isEstimatedMetric(company.harmonizedHeadcount),
      location: locations[0] || "N/A",
      locations,
      domain: company.primaryWebsite || "N/A",
      emails: mergeFoundEmail(company.emails, company.foundEmail),
      foundEmail: company.foundEmail || null,
      ...(Array.isArray(company.leadContacts) ? {leadContacts: company.leadContacts} : {}),
      phone: company.primaryTelephone || "N/A",
      revenue: metricSortValue(company.revenue),
      revenueLabel: company.revenueDisplay || formatRevenueBucket(company.revenue),
    };
  });

export const getLatestEnrichmentSummary = (
  enrichmentColumns,
  latestEnrichmentAction,
  statusMap
) => {
  const actionKey = latestEnrichmentAction || enrichmentColumns[enrichmentColumns.length - 1];
  if (!actionKey) return null;

  const entries = Object.values(statusMap[actionKey] || {}).filter(Boolean);
  if (entries.length === 0) return null;

  const completeCount = entries.filter(
    (entry) => normalizeGeneratedCellStatus(entry.status) === GENERATED_CELL_STATUS.SUCCESS
  ).length;
  const issueCount = entries.filter(
    (entry) => normalizeGeneratedCellStatus(entry.status) === GENERATED_CELL_STATUS.ERROR
  ).length;
  const runningCount = entries.filter((entry) => {
    const status = normalizeGeneratedCellStatus(entry.status);
    return (
      status === GENERATED_CELL_STATUS.LOADING
      || status === GENERATED_CELL_STATUS.QUEUED
    );
  }).length;
  const running = runningCount > 0;

  return {
    color: running || issueCount > 0 ? "warning" : "success",
    label: formatGeneratedColumnProgressLabel({
      label: getEnrichmentActionLabel(actionKey),
      completeCount,
      issueCount,
      totalCount: entries.length,
      running,
    }),
    running,
  };
};

export const isLongFormEnrichmentAction = (actionKey) => {
  if (!actionKey) return false;
  if (actionKey.startsWith("ai_enrichment")) return true;
  if (["find_email", "verify_email", "find_and_verify_email"].includes(actionKey)) return false;
  if (isLeadContactLayoutKey(actionKey)) return false;
  return true;
};

export const getBodyRowHeight = (
  row,
  baseRowHeight,
  enrichedRowHeight,
  enrichmentColumns,
  statusMap
) => {
  if (!row?.id) return baseRowHeight;
  const contactHeight = row.leadContacts?.length && enrichmentColumns.some((column) => isLeadContactLayoutKey(column)
    && statusMap[column]?.[row.id]?.result) ? 80 : baseRowHeight;

  const hasLongFormResult = enrichmentColumns.some((column) => {
    if (!isLongFormEnrichmentAction(column)) return false;
    const entry = statusMap[column]?.[row.id];
    return entry && (entry.status === "success" || entry.status === "failed");
  });
  return Math.max(hasLongFormResult ? enrichedRowHeight : baseRowHeight, contactHeight);
};

export const getContentGridHeight = (
  rows,
  headerHeight,
  baseRowHeight,
  getRowHeight
) => {
  if (rows.length === 0) return headerHeight + baseRowHeight;
  return headerHeight + rows.reduce((sum, row) => sum + getRowHeight(row), 0);
};

export const getActiveColumnKeys = (visibleKeys, defaultVisibleKeys) =>
  visibleKeys.size === 0 ? new Set(defaultVisibleKeys) : visibleKeys;

export const reconcileSortColumns = (
  currentSortColumns,
  activeColumnKeys,
  enrichmentColumns
) => {
  const activeSort = currentSortColumns[0];
  if (!activeSort) return currentSortColumns;
  if (activeSort.columnKey.startsWith("enrich_")) {
    const actionKey = activeSort.columnKey.replace(/^enrich_/, "");
    return enrichmentColumns.includes(actionKey) ? currentSortColumns : [];
  }
  return activeColumnKeys.has(activeSort.columnKey) ? currentSortColumns : [];
};

export const applyEmailVerificationUpdate = (
  currentEmailVerification,
  rowId,
  email,
  update
) => {
  const emailVerification = {...(currentEmailVerification || {})};
  const rowEntries = {...(emailVerification[rowId] || {})};
  rowEntries[email] = {...(rowEntries[email] || {}), ...(update || {})};
  emailVerification[rowId] = rowEntries;

  const scores = Object.values(rowEntries)
    .filter(
      (entry) =>
        entry
        && entry.status === "success"
        && typeof entry.score === "number"
    )
    .map((entry) => entry.score);
  const bestScore = scores.length > 0 ? Math.max(...scores) : null;

  if (bestScore !== null) {
    return {emailVerification, rowStatus: {status: "success", result: bestScore}};
  }
  if (Object.values(rowEntries).some((entry) => entry?.status === "running")) {
    return {emailVerification, rowStatus: {status: "running"}};
  }
  if (Object.values(rowEntries).some((entry) => entry?.status === "failed")) {
    return {
      emailVerification,
      rowStatus: {status: "failed", error: "No successful verifications"},
    };
  }
  return {
    emailVerification,
    rowStatus: {status: "missing", error: "No verified emails"},
  };
};

export const setStatusEntry = (currentStatusMap, actionKey, rowId, update) => {
  const nextStatusMap = {...(currentStatusMap || {})};
  const actionEntries = {...(nextStatusMap[actionKey] || {})};
  actionEntries[rowId] = {...(actionEntries[rowId] || {}), ...(update || {})};
  nextStatusMap[actionKey] = actionEntries;
  return nextStatusMap;
};
